import { defineStore } from "pinia"
import { ElMessage } from "element-plus"
import { uploadDetectionDataApi } from "@/apis/detection"
import { RecordStage } from "@/constants/recordStage"
import { sendFailUsersResultData } from "@/utils/android"
import i18n from "@/utils/i18n"
export const useHrvStore = defineStore("hrv", {
	state: () => ({
		loadModelFlag: false,
		timeDiffArray: [],
		rgbMeanArray: [],
		faceEntityId: "",
		// android初始化参数
		androidInitData: null,
		// 是否开启指夹检测
		fingerDetectionSN: false,
		// init接口数据
		userInitData: {},
		// 眼动数据
		gazePoints: [],
		// 指夹数据
		fingerCollectData: null,
		// rf设备状态
		rfDeviceStatus: null,
		// rf报告数据
		rfReportData: null,
	}),
	getters: {
		LoadModelFlag: (state) => state.loadModelFlag,
		getTimeDiffArray: (state) => state.timeDiffArray,
		getRgbMeanArray: (state) => state.rgbMeanArray,
		getFaceEntityId: (state) => state.faceEntityId,
		getFingerDetectionSN: (state) =>
			state.androidInitData?.collectorSn || "",
		getUserInitData: (state) => state.userInitData,
		getGazePoints: (state) => state.gazePoints,
		getFingerCollectData: (state) => state.fingerCollectData,
		getRFDeviceStatus: (state) => state.rfDeviceStatus,
		getRFReportData: (state) => state.rfReportData,
    getAndroidInitData: (state) => state.androidInitData,
	},

	actions: {
		setLoadModelFlag(flag) {
			this.loadModelFlag = flag
		},
		setTimeDiffArray(timeDiffArray) {
			this.timeDiffArray = timeDiffArray
		},
		setRgbMeanArray(rgbMeanArray) {
			this.rgbMeanArray = rgbMeanArray
		},
		setFaceEntityId(faceEntityId) {
			this.faceEntityId = faceEntityId
		},
		setAndroidInitData(androidInitData) {
			this.androidInitData = androidInitData
		},
		setGazePoints(gazePoints) {
			this.gazePoints = gazePoints
		},
		setUserInitData(userInitData) {
			this.userInitData = userInitData
		},
		setFingerCollectData(fingerCollectData) {
			this.fingerCollectData = fingerCollectData
		},
		setRFDeviceStatus(rfDeviceStatus) {
			this.rfDeviceStatus = rfDeviceStatus
		},
		setRFReportData(rfReportData) {
			console.log("🚀 ~ rfReportData:", rfReportData)
			this.rfReportData = rfReportData
		},
		async sendDataToBackend(router, route) {
			// 错误类型映射
			const ERROR_TYPES = {
				NETWORK: "network",
				SUBMIT: "submit",
				EXPIRE: "expire",
				FRONTEND: "frontend",
				BACKEND: "backend",
			}

			// 状态映射
			const STATUS_MAP = {
				[ERROR_TYPES.NETWORK]: RecordStage.NETWORK_ERROR,
				[ERROR_TYPES.SUBMIT]: RecordStage.SUBMITTING_DATA_ERROR,
				[ERROR_TYPES.EXPIRE]: RecordStage.USER_EXPIRE,
				[ERROR_TYPES.FRONTEND]: RecordStage.FRONTEND_ERROR,
				[ERROR_TYPES.BACKEND]: RecordStage.BACKEND_ERROR,
			}

			// 统一的错误处理函数
			const handleError = async (errorMessage, type) => {
				const backendStatus =
					STATUS_MAP[type] || RecordStage.FRONTEND_ERROR

				router.push({
					name: "HrvFinished",
					query: {
						...route.query,
						process: backendStatus,
					},
				})
				// 如果上传失败，则发送失败数据给android保存，用于后续的补传
				if (type !== ERROR_TYPES.SUBMIT) {
					console.log("🚀 ~ handleError ~ type:", type)
					const requestInfo = buildRequestData("error")
					sendFailUsersResultData(JSON.stringify(requestInfo))
				}
				ElMessage.error(errorMessage)
			}

			// 数据验证
			const validateData = () => {
				if (!this.timeDiffArray?.length || !this.rgbMeanArray?.length) {
					throw {
						message: i18n.global.t("hrv_store_data_incomplete"),
						type: ERROR_TYPES.SUBMIT,
					}
				}
			}

			// 构建请求数据
			const buildRequestData = (type = "success") => {
				console.log("🚀 ~ buildRequestData ~ type:", type)
				const resultArray = this.timeDiffArray.map((time, index) => [
					time,
					...this.rgbMeanArray[index],
				])

				const data = {
					rfResult: this.rfReportData,
					rppgResult: resultArray,
					faceEntityId: this.faceEntityId,
					gazePoints: this.gazePoints,
					ppgResult: this.fingerCollectData,
					assessmentNo: this.userInitData.assessmentNo,
				}
				console.log("🚀 ~ buildRequestData ~ data:", data, type, "type")
				// 当上传失败时，则将用户信息传递给android端
				if (type === "error") {
					data.userInfo = this.userInitData.userContext
					// 后端返回的是小写 但app端接收需要大写
					data.userInfo.pCode = this.userInitData?.userContext?.pcode
				}
				return data
			}

			// 处理API响应
			const handleApiResponse = (response) => {
				const { status, data: apiResult } = response

				// 检查HTTP状态码
				if (status !== 200) {
					throw {
						message: i18n.global.t("hrv_store_http_error", {
							status,
						}),
						type: ERROR_TYPES.NETWORK,
					}
				}

				// 检查业务状态码
				const code = Number(apiResult.code)
				if (code === 401) {
					throw {
						message:
							apiResult?.msg ||
							i18n.global.t("hrv_store_user_expired"),
						type: ERROR_TYPES.EXPIRE,
					}
				}
				if (code === 500) {
					throw {
						message:
							apiResult?.msg ||
							i18n.global.t("hrv_store_submit_failed"),
						type: ERROR_TYPES.SUBMIT,
					}
				}
				if (code !== 0) {
					throw {
						message:
							apiResult?.msg ||
							i18n.global.t("hrv_store_submit_failed"),
						type: ERROR_TYPES.BACKEND,
					}
				}
				console.log(apiResult.data, "apiResult.data")
				return apiResult.data || false
			}

			// 处理成功响应
			const handleSuccess = (data) => {
				console.log("🚀 ~ handleSuccess ~ data:", data)
				if (!data) return
				const {
					reportId: reportIdFromApi,
					showLocalHrvReport,
					hrvDetectMode,
				} = data
				const backendStatus = RecordStage.REPORT_COMPLETE

				if (showLocalHrvReport) {
					router.push({
						name: "HrvReportContiner",
						query: { reportId: reportIdFromApi },
					})
				} else {
					router.push({
						name: "HrvFinished",
						query: {
							...route.query,
							detectMode: hrvDetectMode,
							process: backendStatus,
						},
					})
				}
			}

			try {
				// 数据验证
				validateData()
				// 构建请求数据
				// if(this.rfDeviceStatus === "rfDeviceNotConnected"){

				// }
				const requestInfo = buildRequestData()

				// 发送请求
				const response = await Promise.race([
					uploadDetectionDataApi(requestInfo),
					new Promise((_, reject) =>
						setTimeout(
							() =>
								reject(
									new Error(
										i18n.global.t(
											"hrv_store_request_timeout"
										)
									)
								),
							30000
						)
					),
				])
				console.log("🚀 ~ sendDataToBackend ~ response:", response)

				// 处理响应
				const data = handleApiResponse(response)

				// 处理成功结果
				handleSuccess(data)
			} catch (error) {
				console.error("Failed to send data: ", error)

				// 确定错误类型
				let type = ERROR_TYPES.FRONTEND
				let message =
					error.message || i18n.global.t("hrv_store_unknown_error")

				if (error.type) {
					type = error.type
					message = error.message
				} else if (error?.code === "ECONNABORTED") {
					type = ERROR_TYPES.NETWORK
					message = i18n.global.t("hrv_store_request_timeout")
				} else if (error?.response?.status) {
					type = ERROR_TYPES.NETWORK
					message = i18n.global.t("hrv_store_network_error", {
						status: error.response.status,
					})
				}

				await handleError(message, type)
			}
		},
	},
})
